import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  GraduationCap, 
  Clock, 
  Users, 
  Monitor, 
  Building2, 
  Zap,
  Award,
  Target,
  BookOpen,
  CheckCircle,
  Globe,
  Laptop,
  ArrowRight,
  Star,
  Gift
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import TrainingBookingForm from './TrainingBookingForm';

const TrainingPrograms = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [bookingForm, setBookingForm] = useState({
    isOpen: false,
    trainingLevel: '',
    trainingTitle: ''
  });

  const openBookingForm = (level: string, title: string) => {
    setBookingForm({
      isOpen: true,
      trainingLevel: level,
      trainingTitle: title
    });
  };

  const closeBookingForm = () => {
    setBookingForm({
      isOpen: false,
      trainingLevel: '',
      trainingTitle: ''
    });
  };

  const handleStartFreeTraining = () => {
    navigate('/academy');
  };

  // Comprehensive SEO structured data for Google dominance
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "QuantumBox",
      "url": "https://quantumbox.ai",
      "logo": "https://quantumbox.ai/quantumbox-logo.png",
      "description": "Leading AI training provider offering FREE AI Fundamentals certification and professional AI automation courses. Trusted by 15,000+ students worldwide.",
      "foundingDate": "2023",
      "slogan": "Transforming Business Through AI Innovation",
      "knowsAbout": ["Artificial Intelligence", "Machine Learning", "Prompt Engineering", "AI Automation", "Digital Transformation"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "AI Training Programs",
        "itemListElement": [
          {
            "@type": "Course",
            "name": "AI Fundamentals & Prompt Engineering",
            "price": "0",
            "priceCurrency": "USD",
            "courseMode": "online",
            "isAccessibleForFree": true
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Free AI Fundamentals & Prompt Engineering Certification",
      "description": "Master AI basics and prompt engineering techniques. Get professional certification at zero cost. 15+ hours of expert training.",
      "provider": {
        "@type": "Organization",
        "name": "QuantumBox",
        "url": "https://quantumbox.ai",
        "sameAs": ["https://linkedin.com/company/quantumbox", "https://twitter.com/quantumbox"]
      },
      "courseMode": "online",
      "educationalCredentialAwarded": "Professional AI Certificate",
      "timeRequired": "PT15H",
      "isAccessibleForFree": true,
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "category": "Free Training"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "15420",
        "bestRating": "5"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is the AI Fundamentals training really free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! QuantumBox offers completely free AI Fundamentals & Prompt Engineering training with professional certification included. No hidden costs."
          }
        },
        {
          "@type": "Question", 
          "name": "What makes QuantumBox the best AI training provider?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "QuantumBox combines free world-class training, industry-leading certification, and proven results with 95% career advancement rate and 30% salary increases for graduates."
          }
        }
      ]
    }
  ];

  // Inject comprehensive SEO data for Google dominance
  useEffect(() => {
    // Add multiple structured data schemas
    structuredData.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `quantumbox-schema-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    
    // Add meta tags for competitive advantage
    const metaTags = [
      { name: 'description', content: 'QuantumBox: #1 FREE AI Training Provider. Get certified in AI Fundamentals & Prompt Engineering at zero cost. Professional AI courses L1-L5. 15,000+ successful graduates.' },
      { name: 'keywords', content: 'free AI training, best AI certification, QuantumBox AI, prompt engineering course, AI fundamentals free, professional AI training, machine learning certification' },
      { property: 'og:title', content: 'QuantumBox - World\'s Best FREE AI Training & Certification Platform' },
      { property: 'og:description', content: 'Join 15,000+ professionals who got certified in AI for FREE. QuantumBox offers the industry\'s most comprehensive AI training programs.' }
    ];
    
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.name = tag.name;
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.content = tag.content;
      document.head.appendChild(meta);
    });
    
    return () => {
      // Cleanup
      structuredData.forEach((_, index) => {
        const script = document.getElementById(`quantumbox-schema-${index}`);
        if (script) document.head.removeChild(script);
      });
    };
  }, []);

  const trainingLevels = [
    {
      level: 'L1',
      title: t('training.l1.title'),
      duration: '15 hours',
      description: t('training.l1.description'),
      topics: [t('training.l1.topic1'), t('training.l1.topic2'), t('training.l1.topic3'), t('training.l1.topic4')],
      industries: [t('training.l1.audience1'), t('training.l1.audience2'), t('training.l1.audience3'), t('training.l1.audience4')]
    },
    {
      level: 'L2',
      title: t('training.l2.title'),
      duration: '18 hours',
      description: t('training.l2.description'),
      topics: [t('training.l2.topic1'), t('training.l2.topic2'), t('training.l2.topic3'), t('training.l2.topic4')],
      industries: [t('training.l2.audience1'), t('training.l2.audience2'), t('training.l2.audience3'), t('training.l2.audience4')]
    },
    {
      level: 'L3',
      title: t('training.l3.title'),
      duration: '20 hours',
      description: t('training.l3.description'),
      topics: [t('training.l3.topic1'), t('training.l3.topic2'), t('training.l3.topic3'), t('training.l3.topic4')],
      industries: [t('training.l3.audience1'), t('training.l3.audience2'), t('training.l3.audience3'), t('training.l3.audience4')]
    },
    {
      level: 'L4',
      title: t('training.l4.title'),
      duration: '22 hours',
      description: t('training.l4.description'),
      topics: [t('training.l4.topic1'), t('training.l4.topic2'), t('training.l4.topic3'), t('training.l4.topic4')],
      industries: [t('training.l4.audience1'), t('training.l4.audience2'), t('training.l4.audience3'), t('training.l4.audience4')]
    },
    {
      level: 'L5',
      title: t('training.l5.title'),
      duration: '25 hours',
      description: t('training.l5.description'),
      topics: [t('training.l5.topic1'), t('training.l5.topic2'), t('training.l5.topic3'), t('training.l5.topic4')],
      industries: [t('training.l5.audience1'), t('training.l5.audience2'), t('training.l5.audience3'), t('training.l5.audience4')]
    }
  ];

  const deliveryMethods = [
    {
      icon: Monitor,
      title: t('training.online.title'),
      description: t('training.online.description'),
      features: [t('training.online.feature1'), t('training.online.feature2'), t('training.online.feature3'), t('training.online.feature4')]
    },
    {
      icon: Building2,
      title: t('training.onsite.title'),
      description: t('training.onsite.description'),
      features: [t('training.onsite.feature1'), t('training.onsite.feature2'), t('training.onsite.feature3'), t('training.onsite.feature4')]
    },
    {
      icon: Laptop,
      title: t('training.hybrid.title'),
      description: t('training.hybrid.description'),
      features: [t('training.hybrid.feature1'), t('training.hybrid.feature2'), t('training.hybrid.feature3'), t('training.hybrid.feature4')]
    }
  ];

  return (
    <section className="py-16" id="training-programs" itemScope itemType="https://schema.org/EducationalOrganization">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('training.programs.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-6">
            {t('training.programs.subtitle')}
          </p>
          <div className="flex justify-center mt-8">
            <Button 
              onClick={() => navigate('/training-assessment')}
              size="lg"
              className="bg-gradient-tech text-white shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              <Target className="h-5 w-5 mr-2" />
              Évaluer vos besoins en formation
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>

        {/* Training Levels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {trainingLevels.map((program, index) => {
            const themeClasses = {
              'L1': 'bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 dark:from-emerald-950/40 dark:via-green-950/40 dark:to-teal-950/40 border-emerald-200 dark:border-emerald-800/50',
              'L2': 'bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50 dark:from-blue-950/40 dark:via-indigo-950/40 dark:to-cyan-950/40 border-blue-200 dark:border-blue-800/50', 
              'L3': 'bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50 dark:from-purple-950/40 dark:via-violet-950/40 dark:to-fuchsia-950/40 border-purple-200 dark:border-purple-800/50',
              'L4': 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-orange-950/40 dark:via-amber-950/40 dark:to-yellow-950/40 border-orange-200 dark:border-orange-800/50',
              'L5': 'bg-gradient-to-br from-rose-50 via-pink-50 to-red-50 dark:from-rose-950/40 dark:via-pink-950/40 dark:to-red-950/40 border-rose-200 dark:border-rose-800/50'
            };
            
            const levelBadgeClasses = {
              'L1': 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30',
              'L2': 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30',
              'L3': 'bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg shadow-purple-500/30', 
              'L4': 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/30',
              'L5': 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg shadow-rose-500/30'
            };

            const iconColors = {
              'L1': 'text-emerald-600 dark:text-emerald-400',
              'L2': 'text-blue-600 dark:text-blue-400',
              'L3': 'text-purple-600 dark:text-purple-400',
              'L4': 'text-orange-600 dark:text-orange-400',
              'L5': 'text-rose-600 dark:text-rose-400'
            };

            const iconBgClasses = {
              'L1': 'bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/50 dark:to-teal-900/50',
              'L2': 'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/50 dark:to-indigo-900/50',
              'L3': 'bg-gradient-to-br from-purple-100 to-violet-100 dark:from-purple-900/50 dark:to-violet-900/50',
              'L4': 'bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/50 dark:to-amber-900/50',
              'L5': 'bg-gradient-to-br from-rose-100 to-pink-100 dark:from-rose-900/50 dark:to-pink-900/50'
            };

            return (
              <Card key={index} className={`group hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500 hover:-translate-y-2 h-full relative overflow-hidden border-2 ${themeClasses[program.level]}`}>
                <div className="absolute top-4 right-4 flex flex-col gap-2 z-10">
                  <Badge variant="secondary" className={`${levelBadgeClasses[program.level]} font-bold text-sm px-3 py-1 border-0`}>
                    {program.level}
                  </Badge>
                  {program.level === 'L1' ? (
                     <Badge variant="outline" className="bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 border-emerald-300 font-semibold">
                       {t('training.free')}
                     </Badge>
                   ) : (
                     <Badge variant="outline" className="bg-gradient-to-r from-primary/10 to-primary/20 text-primary border-primary/30 font-semibold">
                       {t('training.professional')}
                     </Badge>
                  )}
                </div>
                <CardHeader className="pb-4 pr-32">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className={`p-4 rounded-xl shadow-lg flex-shrink-0 ${iconBgClasses[program.level]}`}>
                      <GraduationCap className={`h-8 w-8 ${iconColors[program.level]}`} />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {program.title}
                      </CardTitle>
                      <div className="flex items-center space-x-2 mt-1">
                        <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">{program.duration}</span>
                      </div>
                    </div>
                  </div>
                  <CardDescription className="text-base leading-relaxed text-gray-700 dark:text-gray-300 font-medium">
                    {program.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-lg p-4 border border-white/20 dark:border-gray-700/20">
                    <h4 className="font-bold mb-3 flex items-center text-gray-900 dark:text-white">
                      <BookOpen className="h-5 w-5 mr-2" />
                      {t('training.keytopics')}
                    </h4>
                    <div className="space-y-2">
                      {program.topics.map((topic, topicIndex) => (
                        <div key={topicIndex} className="flex items-center space-x-3">
                          <CheckCircle className={`h-4 w-4 flex-shrink-0 ${iconColors[program.level]}`} />
                          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-lg p-4 border border-white/20 dark:border-gray-700/20">
                    <h4 className="font-bold mb-3 flex items-center text-gray-900 dark:text-white">
                      <Users className="h-5 w-5 mr-2" />
                      {t('training.targetaudience')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {program.industries.map((industry, industryIndex) => (
                        <Badge key={industryIndex} variant="outline" className="text-xs font-medium bg-white/80 dark:bg-gray-800/80 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600">
                          {industry}
                        </Badge>
                      ))}
                     </div>
                   </div>
                   
                   {/* Special messaging and buttons */}
                   {program.level === 'L1' ? (
                     <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-6 rounded-lg border border-emerald-200 dark:border-emerald-800/50">
                       <div className="flex items-center mb-3">
                         <Gift className="h-5 w-5 text-emerald-600 mr-2" />
                         <h4 className="font-bold text-emerald-800 dark:text-emerald-300">{t('training.free.message.title')}</h4>
                       </div>
                        <p className="text-sm text-emerald-700 dark:text-emerald-400 mb-4">
                          {t('training.free.message.description')}
                        </p>
                       <Button 
                         onClick={handleStartFreeTraining}
                         className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg"
                       >
                         <Star className="h-4 w-4 mr-2" />
                         {t('training.free.button')}
                         <ArrowRight className="h-4 w-4 ml-2" />
                       </Button>
                     </div>
                   ) : (
                     <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-6 rounded-lg border border-primary/20">
                       <h4 className="font-bold text-gray-900 dark:text-white mb-3">{t('training.professional.title')}</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          {t('training.professional.description')}
                        </p>
                       <Button 
                         onClick={() => openBookingForm(program.level, program.title)}
                         className="w-full"
                       >
                         <Users className="h-4 w-4 mr-2" />
                         {t('training.book.button')}
                         <ArrowRight className="h-4 w-4 ml-2" />
                       </Button>
                     </div>
                   )}
               </CardContent>
             </Card>
             );
          })}
        </div>

        {/* Delivery Methods */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              {t('training.delivery.title')}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {deliveryMethods.map((method, index) => {
              const IconComponent = method.icon;
              return (
                <Card key={index} className="text-center group hover:shadow-elegant transition-smooth">
                  <CardHeader>
                    <div className="flex justify-center mb-4">
                      <div className="p-4 bg-gradient-tech/10 rounded-full">
                        <IconComponent className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <CardTitle className="text-xl">{method.title}</CardTitle>
                    <CardDescription className="text-base">
                      {method.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {method.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center justify-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Certification Benefits */}
        <Card className="bg-gradient-subtle border-0">
          <CardContent className="p-8">
            <div className="text-center">
              <Award className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4">{t('training.certification.title')}</h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                {t('training.certification.description')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary mb-2">95%</div>
                  <div className="text-sm text-muted-foreground">{t('training.certification.advancement')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary mb-2">30%</div>
                  <div className="text-sm text-muted-foreground">{t('training.certification.salary')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary mb-2">85%</div>
                  <div className="text-sm text-muted-foreground">{t('training.certification.placement')}</div>
                </div>
              </div>
            </div>
          </CardContent>
         </Card>
       </div>
       
       {/* Training Booking Form */}
       <TrainingBookingForm
         isOpen={bookingForm.isOpen}
         onClose={closeBookingForm}
         trainingLevel={bookingForm.trainingLevel}
         trainingTitle={bookingForm.trainingTitle}
       />
     </section>
   );
 };

export default TrainingPrograms;